import React, { useContext, useMemo } from "react";
import Select from "react-select";
import { ThemeContext } from "contexts/ThemeContext.jsx";

export default function HusariaSelect(props) {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";

  const { styles: userStyles, ...restProps } = props;

  const defaultStyles = useMemo(
    () => ({
      control: (base, state) => ({
        ...base,
        backgroundColor: state.isDisabled
          ? isDark
            ? "#000000"
            : "#e9ecef"
          : isDark
            ? "#2a2a2a"
            : "#fff",
        color: isDark ? "#f8f9fa" : "#111",
        cursor: state.isDisabled ? "" : "pointer",
        // prevent react-select from overriding cursor on the wrapper
        pointerEvents: state.isDisabled ? "" : base.pointerEvents,
      }),
      valueContainer: (base) => ({
        ...base,
        padding: "2px 8px",
        cursor: "pointer",
      }),
      singleValue: (base, state) => ({
        ...base,
        color: state.isDisabled
          ? isDark
            ? "#000000"
            : "#aaa"
          : isDark
            ? "#f8f9fa"
            : "#111",
      }),
      input: (base) => ({
        ...base,
        color: isDark ? "#f8f9fa" : "#111",
      }),
      placeholder: (base, state) => ({
        ...base,
        color: state.isDisabled
          ? isDark
            ? "#ffffff"
            : "#bbb"
          : isDark
            ? "#f8f9fa"
            : "#6c757d",
        opacity: state.isDisabled ? 1 : isDark ? 0.7 : 1,
      }),
      menu: (base) => ({
        ...base,
        backgroundColor: isDark ? "#2a2a2a" : "#fff",
        border: isDark ? "1px solid #3a3a3a" : base.border,
        zIndex: 9999,
      }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
          ? isDark
            ? "#3a3a3a"
            : "#e9ecef"
          : state.isFocused
            ? isDark
              ? "#333333"
              : "#f6f9fc"
            : "transparent",
        color: isDark ? "#f8f9fa" : "#111",
        cursor: "pointer",
        ":active": {
          backgroundColor: isDark ? "#3a3a3a" : "#e9ecef",
        },
      }),
      multiValue: (base) => ({
        ...base,
        backgroundColor: isDark ? "#3a3a3a" : "#e9ecef",
      }),
      multiValueLabel: (base) => ({
        ...base,
        color: isDark ? "#f8f9fa" : "#111",
      }),
      multiValueRemove: (base) => ({
        ...base,
        color: isDark ? "#f8f9fa" : "#111",
        cursor: "pointer",
        ":hover": {
          backgroundColor: isDark ? "#444444" : "#dee2e6",
          color: isDark ? "#f8f9fa" : "#111",
        },
      }),
      dropdownIndicator: (base) => ({
        ...base,
        cursor: "pointer",
      }),
      clearIndicator: (base) => ({
        ...base,
        cursor: "pointer",
      }),
      indicatorSeparator: (base) => ({
        ...base,
      }),
      menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
      }),
    }),
    [isDark],
  );

  const mergedStyles = useMemo(() => {
    if (!userStyles) return defaultStyles;

    const merged = { ...defaultStyles };

    Object.keys(userStyles).forEach((key) => {
      const defaultStyleFn = defaultStyles[key];
      const userStyleFn = userStyles[key];

      if (typeof userStyleFn === "function") {
        merged[key] = (base, state) => {
          const defaultResult =
            typeof defaultStyleFn === "function"
              ? defaultStyleFn(base, state)
              : base;
          return userStyleFn(defaultResult, state);
        };
      } else {
        merged[key] = userStyleFn;
      }
    });

    return merged;
  }, [defaultStyles, userStyles]);

  return (
    <Select
      {...restProps}
      styles={mergedStyles}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      menuPlacement="bottom"
    />
  );
}
